import { onBeforeUnmount, onMounted, watch, type Ref } from 'vue'

import type { usePhoneInput } from '~/composables/usePhoneInput'
import { PhoneNumber } from '~/utils/PhoneNumber'

interface PhoneFieldSyncOptions {
  /** The model value the parent currently holds. */
  readonly modelValue: () => string
  readonly field: ReturnType<typeof usePhoneInput>
  readonly input: Ref<HTMLInputElement | null>
  readonly emitBlur: () => void
  readonly emitUpdate: (entry: string) => void
}

interface PhoneFieldSync {
  readonly onBlur: () => void
  readonly onInput: (event: Event) => void
  readonly reconcileNativeValue: () => void
  readonly validateReconciled: () => boolean
}

/** Keeps a phone input and its parent model in step: publishes edits once, adopts parent updates, and replays eventless autofill. */
export function usePhoneFieldSync(options: PhoneFieldSyncOptions): PhoneFieldSync {
  const { field, input } = options

  // The last value handed to the parent, so one edit never publishes twice.
  let published = ''
  const publish = (entry: string): void => {
    if (entry !== published) {
      published = entry
      options.emitUpdate(entry)
    }
  }

  const onInput = (event: Event): void => {
    const rawValue =
      event.currentTarget instanceof HTMLInputElement ? event.currentTarget.value : ''
    field.handleInput(event)
    // The visible text reverts on a rejected character; the model carries the rejected entry so
    // every form holding this value sees an invalid phone.
    publish(PhoneNumber.hasInvalidCharacters(rawValue) ? rawValue : field.phoneValue.value)
  }

  // Autofill can set the native value without an input event; replay it through the input path.
  const reconcileNativeValue = (): void => {
    const element = input.value
    if (element !== null && element.value !== field.phoneValue.value) {
      element.dispatchEvent(new Event('input'))
    }
  }

  // A submit reads form state, so the replay runs before the form's own handlers.
  const reconcileAtSubmit = (): void => {
    reconcileNativeValue()
    field.validate()
  }
  onMounted(() => {
    input.value?.form?.addEventListener('submit', reconcileAtSubmit, true)
  })
  onBeforeUnmount(() => {
    input.value?.form?.removeEventListener('submit', reconcileAtSubmit, true)
  })

  watch(
    options.modelValue,
    (value) => {
      published = value
      field.syncExternal(value)
    },
    { immediate: true }
  )
  watch(field.phoneValue, publish)

  return {
    onBlur: () => {
      reconcileNativeValue()
      field.handleBlur()
      options.emitBlur()
    },
    onInput,
    reconcileNativeValue,
    validateReconciled: () => {
      reconcileNativeValue()
      return field.validate()
    }
  }
}
